import { useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import Editor from '@monaco-editor/react';
import { useTemplatesStudio } from 'api/useTemplatesStudio';
import { useUserDetails } from 'api/useUserDetails';
import { EEventsType } from 'constants/enums';
import {
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH
} from 'constants/templateStudio';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { getApiErrorMessage } from 'utils/errorsTextHelper';
import { trackMixpanelEvent } from 'utils/mixpanelClient';

import Button from 'components/Button/Button';
import { cn } from 'components/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';

import { getDefaultPersonalizationJson } from './defaults';
import { usePanelResize } from './usePanelResize.hook';

import './errorLineStyles.css';

export const TEST_PERSONALIZATION_WIDTH = 420;
export const TEST_PERSONALIZATION_MARGIN_RIGHT_PX = 416;
const TOKEN_VALIDITY_MS = 115 * 60 * 1000;
const STORAGE_KEY_PREFIX = 'test_personalization_json_';
const PREVIEW_RESPONSE_STORAGE_KEY_PREFIX =
  'test_personalization_preview_response_';
const TOOLBAR_ICON_BTN_CLASS =
  'flex w-7 h-7 px-3 py-2 justify-center items-center gap-2 rounded-md bg-base-muted text-base-secondaryForeground';
const TOOLTIP_CONTENT_CLASS =
  'rounded-md border border-base-popover bg-base-popover shadow-md';

const EDITOR_OPTIONS = {
  minimap: { enabled: false },
  fontSize: 14,
  formatOnPaste: true,
  scrollBeyondLastLine: false,
  formatOnType: true,
  showFoldingControls: 'always' as const
};

export { PANEL_MIN_WIDTH };
const PANEL_DEFAULT_WIDTH = 416;
const PANEL_MAX_VIEWPORT_RATIO = 0.7;
const PANEL_RIGHT_MARGIN = 6;
const MIN_FORM_CONTENT_WIDTH = 400;

function formatJsonString(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

export const TestPersonalizationContainer: React.FC<{
  hasSpaceForInFlow?: boolean;
}> = ({ hasSpaceForInFlow = true }) => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const {
    isPersonalizationOpen,
    setIsPersonalizationOpen,
    personalizationJson,
    setPersonalizationJson,
    generatedOffersJson,
    setPreviewOffersResponse,
    personalizationPanelWidth,
    setPersonalizationPanelWidth
  } = useTemplateStudioState();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { userDetails } = useUserDetails();

  const { getPreviewToken, getOffersPreview } = useTemplatesStudio(publisherId);

  const editorRef = useRef<any>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const monacoRef = useRef<any>(null);
  const decorationsRef = useRef<any>([]);
  const initializedRef = useRef(false);
  const defaultJsonRef = useRef('');
  const initialDefaultJsonRef = useRef<string>('');
  const valueWhenPanelOpenedRef = useRef<string>('');
  const cachedTokenRef = useRef<{ token: string; expiresAt: number } | null>(
    null
  );
  const tokenFetchInProgressRef = useRef(false);
  const [editorValue, setEditorValue] = useState('');
  const [jsonError, setJsonError] = useState<{ message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const storageKey = `${STORAGE_KEY_PREFIX}${publisherId}`;

  const setEditorMarkers = useCallback(
    (errorInfo: { line: number; column: number; message: string } | null) => {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      if (!editor || !monaco) return;

      const model = editor.getModel();
      if (!model) return;

      decorationsRef.current = editor.deltaDecorations(
        decorationsRef.current,
        []
      );

      if (!errorInfo) {
        monaco.editor.setModelMarkers(model, 'json', []);
        return;
      }

      const { line, column, message } = errorInfo;

      monaco.editor.setModelMarkers(model, 'json', [
        {
          startLineNumber: line,
          startColumn: column,
          endLineNumber: line,
          endColumn: column + 1,
          message,
          severity: monaco.MarkerSeverity.Error
        }
      ]);

      decorationsRef.current = editor.deltaDecorations(
        [],
        [
          {
            range: new monaco.Range(line, 1, line, 1),
            options: {
              isWholeLine: true,
              className: 'errorLineHighlight',
              glyphMarginClassName: 'errorLineGlyph'
            }
          }
        ]
      );
    },
    []
  );

  const validateJson = useCallback(
    (value: string): boolean => {
      try {
        JSON.parse(value);
        setJsonError(null);
        setEditorMarkers(null);
        return true;
      } catch (error) {
        if (error instanceof SyntaxError) {
          const match = error.message.match(/position (\d+)/);
          if (match) {
            const position = parseInt(match[1], 10);
            const lines = value.substring(0, position).split('\n');
            const line = lines.length;
            const column = lines[lines.length - 1].length + 1;
            const message = `Invalid JSON at line ${line}, column ${column}`;
            setJsonError({ message });
            setEditorMarkers({ line, column, message });
          } else {
            setJsonError({ message: 'Invalid JSON format' });
            setEditorMarkers(null);
          }
        } else {
          setJsonError({ message: 'Invalid JSON format' });
          setEditorMarkers(null);
        }
        return false;
      }
    },
    [setEditorMarkers]
  );

  useEffect(() => {
    if (isPersonalizationOpen && !initializedRef.current) {
      const defaultRaw = generatedOffersJson || getDefaultPersonalizationJson();
      const formattedDefault = formatJsonString(defaultRaw);
      defaultJsonRef.current = formattedDefault;
      initialDefaultJsonRef.current = formattedDefault;

      const rawValue =
        sessionStorage.getItem(storageKey) || personalizationJson || defaultRaw;
      const initialValue = formatJsonString(rawValue);
      setEditorValue(initialValue);
      valueWhenPanelOpenedRef.current = initialValue;
      validateJson(initialValue);
      initializedRef.current = true;
    }
    if (!isPersonalizationOpen) {
      initializedRef.current = false;
      initialDefaultJsonRef.current = '';
    }
  }, [
    isPersonalizationOpen,
    personalizationJson,
    generatedOffersJson,
    validateJson,
    storageKey
  ]);

  useEffect(() => {
    if (
      !isPersonalizationOpen ||
      !initializedRef.current ||
      !generatedOffersJson
    )
      return;
    const newDefault = formatJsonString(generatedOffersJson);
    if (newDefault === defaultJsonRef.current) return;
    if (editorValue !== defaultJsonRef.current) return;
    defaultJsonRef.current = newDefault;
    initialDefaultJsonRef.current = newDefault;
    setEditorValue(newDefault);
    validateJson(newDefault);
    editorRef.current?.setValue(newDefault);
  }, [isPersonalizationOpen, generatedOffersJson, editorValue, validateJson]);

  useEffect(() => {
    if (isPersonalizationOpen) {
      setPersonalizationPanelWidth(PANEL_MIN_WIDTH);
    }
  }, [isPersonalizationOpen, setPersonalizationPanelWidth]);

  useEffect(() => {
    if (isPersonalizationOpen) {
      const now = Date.now();
      const hasValidToken =
        cachedTokenRef.current && cachedTokenRef.current.expiresAt > now;

      if (!hasValidToken && !tokenFetchInProgressRef.current) {
        tokenFetchInProgressRef.current = true;
        getPreviewToken
          .mutateAsync()
          .then((tokenResponse) => {
            if (tokenResponse?.token) {
              cachedTokenRef.current = {
                token: tokenResponse.token,
                expiresAt: Date.now() + TOKEN_VALIDITY_MS
              };
            }
          })
          .catch((error) => {
            console.error('Failed to pre-fetch preview token:', error);
          })
          .finally(() => {
            tokenFetchInProgressRef.current = false;
          });
      }
    }
  }, [isPersonalizationOpen]);

  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      const newValue = value ?? '';
      setEditorValue(newValue);
      validateJson(newValue);
    },
    [validateJson]
  );

  const applyEditorValue = useCallback(
    (value: string) => {
      setEditorValue(value);
      validateJson(value);
      editorRef.current?.setValue(value);
    },
    [validateJson]
  );

  const handleCopy = useCallback(async () => {
    try {
      const editor = editorRef.current;
      let textToCopy = editorValue;

      if (editor) {
        const selection = editor.getSelection();
        if (selection && !selection.isEmpty()) {
          textToCopy =
            editor.getModel()?.getValueInRange(selection) || editorValue;
        }
      }

      await navigator.clipboard.writeText(textToCopy);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, [editorValue]);

  const handleFormat = useCallback(() => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  }, []);

  const handleClear = useCallback(() => {
    const revertedJson =
      valueWhenPanelOpenedRef.current ||
      initialDefaultJsonRef.current ||
      formatJsonString(generatedOffersJson || getDefaultPersonalizationJson());
    const formattedJson = formatJsonString(revertedJson);
    applyEditorValue(formattedJson);
    setPersonalizationJson(formattedJson);
  }, [applyEditorValue, setPersonalizationJson, generatedOffersJson]);

  const handleClose = useCallback(() => {
    sessionStorage.setItem(storageKey, editorValue);
    setIsPersonalizationOpen(false);
  }, [editorValue, setIsPersonalizationOpen, storageKey]);

  const getValidToken = useCallback(async (): Promise<string> => {
    const now = Date.now();

    if (cachedTokenRef.current && cachedTokenRef.current.expiresAt > now) {
      return cachedTokenRef.current.token;
    }

    const tokenResponse = await getPreviewToken.mutateAsync();
    const token = tokenResponse?.token;

    if (!token) {
      throw new Error('Failed to obtain preview token');
    }

    cachedTokenRef.current = {
      token,
      expiresAt: now + TOKEN_VALIDITY_MS
    };

    return token;
  }, [getPreviewToken]);

  const handlePreview = useCallback(async () => {
    if (!validateJson(editorValue)) {
      return;
    }

    sessionStorage.setItem(storageKey, editorValue);
    setIsLoading(true);
    setPreviewError(null);

    try {
      const token = await getValidToken();

      const playerInfoSync = JSON.parse(editorValue) as Record<string, unknown>;
      const sanitized = { ...playerInfoSync };
      if (sanitized.offersOrder === '') {
        delete sanitized.offersOrder;
      }
      const pl = sanitized.playerLevel as Record<string, unknown> | undefined;
      if (pl && typeof pl === 'object' && !String(pl.assetId ?? '').trim()) {
        delete sanitized.playerLevel;
      }

      const previewResponse = await getOffersPreview.mutateAsync({
        token,
        playerInfoSync: sanitized
      });

      const responseToStore = previewResponse as unknown as Record<
        string,
        unknown
      >;
      setPreviewOffersResponse(responseToStore);
      try {
        sessionStorage.setItem(
          `${PREVIEW_RESPONSE_STORAGE_KEY_PREFIX}${publisherId}`,
          JSON.stringify(responseToStore)
        );

        trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
          publisher_id: publisherId,
          user_id: userDetails?.userId,
          company_id: userDetails?.company,
          action: 'json_preview',
          is_active: false
        });
        trackBigQueryEvent({
          eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
          publisherId: publisherId,
          userId: userDetails?.userId,
          action: 'json_preview',
          is_active: false
        });
      } catch {
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      setPreviewError(errorMessage);
      console.error('Preview error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [editorValue, getOffersPreview, storageKey]);

  const getMaxWidth = useCallback(() => {
    const viewport = document.documentElement.clientWidth;
    return Math.min(
      PANEL_MAX_WIDTH,
      Math.floor(viewport * PANEL_MAX_VIEWPORT_RATIO),
      viewport - MIN_FORM_CONTENT_WIDTH - PANEL_RIGHT_MARGIN
    );
  }, []);

  const handleResizeMouseDown = usePanelResize({
    minWidth: PANEL_MIN_WIDTH,
    getMaxWidth,
    onWidthChange: setPersonalizationPanelWidth,
    rightMargin: PANEL_RIGHT_MARGIN,
    panelRef
  });

  useEffect(() => {
    if (!isPersonalizationOpen) return;
    document.body.style.overflowX = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflowX = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isPersonalizationOpen, handleClose]);

  useEffect(() => {
    if (!isPersonalizationOpen) return;
    const clamp = () => {
      const viewport = document.documentElement.clientWidth;
      const maxAllowed = viewport - MIN_FORM_CONTENT_WIDTH - PANEL_RIGHT_MARGIN;
      const current = personalizationPanelWidth ?? PANEL_DEFAULT_WIDTH;
      if (current > maxAllowed) {
        setPersonalizationPanelWidth(
          Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, maxAllowed))
        );
      }
    };
    window.addEventListener('resize', clamp);
    clamp();
    return () => window.removeEventListener('resize', clamp);
  }, [isPersonalizationOpen, personalizationPanelWidth, setPersonalizationPanelWidth]);

  if (!isPersonalizationOpen) {
    return null;
  }

  const panelWidth = personalizationPanelWidth ?? PANEL_DEFAULT_WIDTH;
  const effectiveWidth = Math.min(panelWidth, getMaxWidth());

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[1001] top-[72px] bottom-[5px] right-[6px] flex animate-in slide-in-from-right duration-300 overflow-hidden"
      style={{
        width: effectiveWidth,
        maxWidth: getMaxWidth()
      }}
    >
      <div
        role="separator"
        aria-label="Resize panel"
        onPointerDown={handleResizeMouseDown}
        className="shrink-0 w-1.5 cursor-col-resize select-none touch-none hover:bg-neutral-200 active:bg-indigo-200 transition-colors"
      />
      <div
          className={cn(
            'flex-1 min-w-0 bg-white p-4 flex flex-col',
            hasSpaceForInFlow ? 'pl-[18px]' : 'pl-4'
          )}
        >
        <div className="shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">
            Test personalization response
          </h2>
          <button onClick={handleClose} className="rounded-sm">
            <FontAwesomeIcon
              icon="fa-regular fa-xmark"
              className="text-black"
            />
            <span className="sr-only">Close</span>
          </button>
        </div>
        <div className="flex items-center justify-between pt-3">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleFormat}
                  className={TOOLBAR_ICON_BTN_CLASS}
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-list-tree"
                    className="text-sm text-base-secondaryForeground"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                className={TOOLTIP_CONTENT_CLASS}
              >
                <p className="text-xs">Format</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                  className={TOOLBAR_ICON_BTN_CLASS}
                >
                  <FontAwesomeIcon
                    icon="fa-regular fa-copy"
                    className="text-sm text-base-secondaryForeground"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className={TOOLTIP_CONTENT_CLASS}>
                <p className="text-xs">Copy</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Button variant="link" size="sm" onClick={handleClear}>
            Clear
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-3">
        <div className="h-full rounded-lg border border-neutral-200 overflow-hidden">
          <Editor
            height="100%"
            defaultLanguage="json"
            theme="vs"
            value={editorValue}
            onChange={(val) => handleEditorChange(val)}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
              monacoRef.current = monaco;
            }}
            options={EDITOR_OPTIONS}
          />
        </div>
      </div>

      <div className="shrink-0 pt-4 flex items-center justify-between gap-[10px]">
        <p
          className={cn(
            'flex-1 min-w-0 break-words text-sm',
            jsonError || previewError ? 'text-red-500' : 'text-transparent'
          )}
        >
          {jsonError?.message || previewError || ''}
        </p>
        <Button
          onClick={handlePreview}
          disabled={!!jsonError || isLoading}
          className="rounded-lg bg-indigo-600 hover:bg-indigo-700 shadow-[0_0_5px_0_rgba(79,70,229,0.50)]"
        >
          {isLoading ? 'Loading...' : 'Preview'}
        </Button>
      </div>
      </div>
    </div>,
    document.body
  );
};
